/**
 * ATA Client Onboarding — Fluent UI 9 brand theme
 * --------------------------------------------------------------
 * Single source of truth for the ATA navy brand. Drop this in,
 * wrap the app in <FluentProvider theme={ataLightTheme}>, and the
 * navy automatically drives primary buttons, toggles, selected
 * rows, focus rings, links, etc.
 *
 * Light theme only for now. A dark variant can be added later via
 * createDarkTheme(ataBrand).
 */

import { BrandVariants, createLightTheme, Theme } from '@fluentui/react-components';
import { ClientType } from '../api';

/**
 * 16-step ATA navy brand ramp.
 * Convention: brand[80] is the primary (colorBrandBackground in light theme),
 * brand[70]/[60] are hover/pressed, brand[150]/[160] are the light tints used
 * for selected backgrounds. Tuned to anchor at ATA navy #103a6e.
 */
export const ataBrand: BrandVariants = {
    10: '#03090F',
    20: '#061626',
    30: '#08203C',
    40: '#0A294E',
    50: '#0B2F5B',
    60: '#0C3463', // pressed  (colorBrandBackgroundPressed)
    70: '#0E3768', // hover    (colorBrandBackgroundHover)
    80: '#103A6E', // PRIMARY  (colorBrandBackground) — ATA navy
    90: '#21507F',
    100: '#2A5C97',
    110: '#3A6EAD',
    120: '#5283C0',
    130: '#7099D0',
    140: '#97B6E0',
    150: '#C2D5EF',
    160: '#EAF1FB', // lightest wash — matches --brand-tint in the prototype
};

export const ataLightTheme: Theme = {
    ...createLightTheme(ataBrand),

    // Slightly softer, cooler neutrals to match the prototype.
    colorNeutralBackground3: '#F1F3F5', // app canvas  (--canvas)
    borderRadiusMedium: '7px', // (--r-md)  controls/buttons
    borderRadiusLarge: '10px', // (--r-lg)  cards
};

/**
 * Client-type categorical palette (ATA-Design-Standard.md §5).
 * NOT brand tokens — a fixed, low-saturation palette so client types read as
 * data categories distinct from the navy brand. Used for directory type badges
 * and client avatars. Keyed by the ClientType enum value.
 */
export interface ClientTypeColors {
    /** Text / foreground color for the badge label + avatar initials. */
    text: string;
    /** Tint background for the badge + avatar. */
    tint: string;
    /** Status dot color. */
    dot: string;
}

export const clientTypeColors: Record<ClientType, ClientTypeColors> = {
    [ClientType.Individual]: { text: '#1C4F9C', tint: '#EAF1FB', dot: '#2F6FD1' },
    [ClientType.Corporation]: { text: '#4B3A9E', tint: '#EEECFB', dot: '#6B54C9' },
    [ClientType.Partnership]: { text: '#1D6B63', tint: '#E3F3F1', dot: '#2F9488' },
    [ClientType.Fiduciary]: { text: '#8A5A16', tint: '#FBF0DB', dot: '#C98A2E' },
    [ClientType.NonProfit]: { text: '#2C6B3F', tint: '#E6F4EA', dot: '#41995C' },
    [ClientType.EmployeePlan]: { text: '#7D3781', tint: '#F4E8F5', dot: '#AA52AD' },
    [ClientType.SoleProprietorship]: { text: '#1C5E7C', tint: '#E2F1F7', dot: '#2F86AD' },
    [ClientType.Staff]: { text: '#4A525E', tint: '#ECEEF1', dot: '#6B7480' },
};

/** Neutral fallback for any unknown / missing client type. */
export const clientTypeColorsFallback: ClientTypeColors = {
    text: '#4A525E',
    tint: '#ECEEF1',
    dot: '#6B7480',
};

export const getClientTypeColors = (clientType?: ClientType | string): ClientTypeColors =>
    clientTypeColors[clientType as ClientType] ?? clientTypeColorsFallback;

/**
 * Human-readable client-type labels. Most match the enum value verbatim; only
 * SoleProprietorship needs spacing.
 */
export const clientTypeLabels: Record<ClientType, string> = {
    [ClientType.Individual]: 'Individual',
    [ClientType.Corporation]: 'Corporation',
    [ClientType.Partnership]: 'Partnership',
    [ClientType.Fiduciary]: 'Fiduciary',
    [ClientType.NonProfit]: 'Non-Profit',
    [ClientType.EmployeePlan]: 'Employee Plan',
    [ClientType.SoleProprietorship]: 'Sole Proprietorship',
    [ClientType.Staff]: 'Staff',
};

export const getClientTypeLabel = (clientType?: ClientType | string): string =>
    clientTypeLabels[clientType as ClientType] ?? String(clientType ?? '');
